import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { InstallBanner } from "@/components/InstallBanner";

export const Enso = ({ size = 34, glow = true }) => (
  <img src="/orec-emblem.png" alt="O.R.E.C emblem" width={size} height={size}
    className={`rounded-full object-cover shrink-0 ${glow ? "enso-glow" : ""}`} style={{ width: size, height: size }} />
);

const navLinks = [
  { to: "/browse", label: "Explore" },
  { to: "/featured", label: "Featured" },
  { to: "/learn", label: "Learn Library" },
  { to: "/corner", label: "Bridgett's Corner" },
  { to: "/community", label: "Community" },
  { to: "/membership", label: "Membership" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashPath = user?.role === "admin" ? "/admin" : user?.role === "practitioner" ? "/dashboard" : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-4">
            <Enso size={48} />
            <div className="leading-tight">
              <span className="font-display text-2xl font-semibold tracking-[0.2em] text-foreground">O.R.E.C</span>
              <p className="hidden sm:block text-[11px] uppercase tracking-[0.24em] text-muted-foreground mt-0.5">Our Ripple Effect Community</p>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <Button variant="ghost" size="sm" data-testid="nav-bookings-btn" onClick={() => navigate("/bookings")}>My Bookings</Button>
                <Button variant="ghost" size="sm" data-testid="nav-ripples-btn" onClick={() => navigate("/ripples")}>My Ripples</Button>
                {dashPath && (
                  <Button variant="outline" size="sm" data-testid="nav-dashboard-btn" onClick={() => navigate(dashPath)}>
                    {user.role === "admin" ? "Admin" : "My Studio"}
                  </Button>
                )}
                <Button variant="ghost" size="sm" data-testid="nav-logout-btn" onClick={logout}>Sign out</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" data-testid="nav-login-btn" onClick={() => navigate("/login")}>Sign in</Button>
                <Button size="sm" data-testid="nav-join-btn" className="rounded-full px-5" onClick={() => navigate("/register")}>Join</Button>
              </>
            )}
          </div>
          <button className="md:hidden text-foreground" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        <nav className="hidden md:block border-t border-border/40" data-testid="nav-links-bar">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 h-11 flex items-center gap-10">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => `text-xs uppercase tracking-[0.18em] transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        </nav>
        {open && (
          <div className="md:hidden border-t border-border px-5 py-4 space-y-3 bg-background" data-testid="nav-mobile-menu">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm text-muted-foreground">{l.label}</NavLink>
            ))}
            <Link to="/get-the-app" onClick={() => setOpen(false)} className="block text-sm text-primary" data-testid="nav-mobile-get-app">Get the app</Link>
            {user ? (
              <>
                <button className="block text-sm text-muted-foreground" onClick={() => { setOpen(false); navigate("/bookings"); }}>My Bookings</button>
                <button className="block text-sm text-muted-foreground" onClick={() => { setOpen(false); navigate("/ripples"); }}>My Ripples</button>
                {dashPath && <button className="block text-sm text-primary" onClick={() => { setOpen(false); navigate(dashPath); }}>{user.role === "admin" ? "Admin" : "My Studio"}</button>}
                <button className="block text-sm text-muted-foreground" onClick={() => { setOpen(false); logout(); }}>Sign out</button>
              </>
            ) : (
              <>
                <button className="block text-sm text-muted-foreground" onClick={() => { setOpen(false); navigate("/login"); }}>Sign in</button>
                <button className="block text-sm text-primary" onClick={() => { setOpen(false); navigate("/register"); }}>Join</button>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <InstallBanner />

      <footer className="border-t border-border mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-3"><Enso size={26} /><span className="font-display text-lg tracking-[0.18em]">O.R.E.C</span></div>
            <p className="text-sm text-muted-foreground leading-relaxed">A growing community of vetted practitioners and businesses — good people doing good work. Starting in Calgary.</p>
          </div>
          <div className="text-sm space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Explore</p>
            <Link to="/browse" className="block text-muted-foreground hover:text-foreground">Find a practitioner</Link>
            <Link to="/how-booking-works" className="block text-muted-foreground hover:text-foreground" data-testid="footer-seeker-faq-link">How booking works</Link>
            <Link to="/learn" className="block text-muted-foreground hover:text-foreground">Learn Library</Link>
            <Link to="/corner" className="block text-muted-foreground hover:text-foreground">Bridgett's Corner</Link>
            <Link to="/register" className="block text-muted-foreground hover:text-foreground">List your practice</Link>
            <Link to="/how-joining-works" className="block text-muted-foreground hover:text-foreground" data-testid="footer-faq-link">How joining works</Link>
            <Link to="/policies" className="block text-muted-foreground hover:text-foreground" data-testid="footer-policies-link">Cancellations & refunds</Link>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 pt-4">Contact</p>
            <Link to="/contact" className="block text-muted-foreground hover:text-foreground" data-testid="footer-contact-link">Send a message</Link>
            <Link to="/email-preferences" className="block text-muted-foreground hover:text-foreground" data-testid="footer-email-prefs-link">Email preferences</Link>
            <Link to="/get-the-app" className="block text-primary hover:underline" data-testid="footer-get-app-link">Get the app</Link>
          </div>
          <div className="text-xs text-muted-foreground leading-relaxed" data-testid="footer-disclaimer">
            <p className="text-xs uppercase tracking-[0.2em] mb-3">Disclaimer</p>
            <p>Content and services on O.R.E.C are offered for wellness and educational purposes only and do not constitute medical, legal, or financial advice. Always consult a qualified professional for health concerns.</p>
            <p className="mt-4">© {new Date().getFullYear()} O.R.E.C — Our Ripple Effect Community. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
